import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { generateEmailReply } from "@/lib/generate-email-reply";
import { useLanguage } from "@/contexts/LanguageContext";

/** Draft-only: the shared composer remains the only send path. Never overwrite edits made during generation. */
export function InlineDraftControl({
  issueId,
  draft,
  onDraft,
  disabled,
  actionContainer,
}: {
  issueId: string;
  draft: string;
  onDraft: (value: string) => void;
  disabled?: boolean;
  actionContainer?: HTMLElement | null;
}) {
  const { t } = useLanguage();
  const [instruction, setInstruction] = useState("");
  const [pending, setPending] = useState(false);
  const latest = useRef(draft);
  latest.current = draft;
  const epoch = useRef(0);
  useEffect(
    () => () => {
      epoch.current++;
    },
    [issueId]
  );
  const generate = async () => {
    if (pending || disabled) return;
    const snapshot = draft;
    const note = instruction.trim();
    const prompt = snapshot.trim()
      ? `Revise the following draft according to the instruction. Preserve everything the instruction does not change.\nInstruction: ${note || "Improve clarity and tone."}\nCurrent draft:\n${snapshot}`
      : note;
    if (prompt.length > 2000) {
      toast.error(
        t(
          "support.ai.tooLong",
          "The draft and instruction are too long. Please shorten them."
        )
      );
      return;
    }
    const request = ++epoch.current;
    setPending(true);
    try {
      const result = await generateEmailReply(issueId, prompt);
      if (request !== epoch.current) return;
      if (latest.current !== snapshot) {
        toast.info(
          t(
            "support.ai.draftChanged",
            "Your draft changed while generating. Please apply again."
          )
        );
        return;
      }
      if (result.action === "no_reply") {
        toast.info(
          t("support.ai.noReply", "No reply is recommended for this message.")
        );
        return;
      }
      onDraft(result.replyBody);
      setInstruction("");
    } catch {
      if (request === epoch.current)
        toast.error(
          t(
            "support.ai.failed",
            "Couldn't create the AI draft. Your text has been kept."
          )
        );
    } finally {
      if (request === epoch.current) setPending(false);
    }
  };
  return (
    <div className="support-ai-instruction">
      {pending ? (
        <Loader2 size={14} className="animate-spin" />
      ) : (
        <Sparkles size={14} />
      )}
      <input
        aria-label={t("support.ai.instruction", "AI instruction")}
        value={instruction}
        maxLength={1500}
        disabled={pending || disabled}
        placeholder={
          draft.trim()
            ? t("support.ai.changeDraft", "What should AI change in the draft?")
            : t(
                "support.ai.writePrompt",
                "What should AI write? e.g. “Technician arrives tomorrow morning”"
              )
        }
        onChange={(e) => setInstruction(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.nativeEvent.isComposing) {
            e.preventDefault();
            void generate();
          }
        }}
      />
      {(actionContainer === undefined || instruction.trim()) && (
        <button
          type="button"
          disabled={pending || disabled}
          onClick={() => void generate()}
        >
          {pending
            ? t("support.ai.drafting", "Drafting…")
            : instruction.trim()
              ? t("common.apply", "Apply")
              : t("support.ai.draft", "AI draft")}
        </button>
      )}
      {actionContainer &&
        createPortal(
          <button
            type="button"
            className="support-ai-draft-action"
            disabled={pending || disabled}
            onClick={() => void generate()}
          >
            <Sparkles size={12} />
            {pending
              ? t("support.ai.drafting", "Drafting…")
              : t("support.ai.draft", "AI draft")}
          </button>,
          actionContainer
        )}
    </div>
  );
}
